import React, { useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { shallowEqual, useSelector } from 'react-redux';
import Select from '../../../components/form/Select';
import PERMISSION from '../../../constants/Permissions';
import { UNIT_ASSIGNMENT_MODALS } from '../../../constants/UnitAssignment';
import { SHIFT_STATUS } from '../../../constants/ShiftStatus';
import Utils from '../../../utils/utils';
import { user } from '../../../redux/user';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../../constants/ManagementCompaniesPermissions';

const ActionsDropdown = ({
	canGuarantee,
	getUnitName,
	hasAvailableUnits,
	hasUnitsEnabled,
	onCancel,
	onConfirm,
	onGuaranteedCheckboxChange,
	onGuaranteeBundleChange,
	permissions,
	setShowUnitAssignmentModal,
	setUnitAssignmentSlot,
	slot,
	isFlex = false,
	onAssign,
	onRouteSlot,
	onFacilityFloatSlot,
	onMarkSlotAsCallback,
	onSplitShift
}) => {
	const userData = useSelector(user, shallowEqual);
	const hasEditAssignmentPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.EDIT_ASSIGNMENT, permissions), [
		permissions
	]);
	const hasFlexEditUnitAssignmentPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.FLEX_ASSIGN_UNITS, permissions),
		[permissions]
	);
	const hasEditStatusPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.EDIT, permissions), [permissions]);
	const hasFlexCancelPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.FLEX_CANCEL, permissions), [
		permissions
	]);
	const hasCancelPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.CANCEL, permissions), [permissions]);
	const hasToggleGuaranteePermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.GUARANTEE, permissions), [
		permissions
	]);

	const hasFlexCaregiverAssignmentPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.FLEX_ADD_CAREGIVER, permissions),
		[permissions]
	);

	const hasRouteFlexPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.PORTAL_EDIT_SHIFT_ROUTING, permissions),
		[permissions]
	);

	const hasMarkAsCallbackPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.ON_CALL_SHIFT_MARK_AS_CALLBACK, permissions),
		[permissions]
	);
	const editFlexShiftFacilitiesForManagementCompany = useMemo(
		() =>
			userData.facilitiesEnabled.filter(
				(facility) =>
					facility.management_company_id === slot.management_company_id &&
					!!facility.allow_flex_shifts &&
					userData.permissionsByFacility[facility.id]?.includes(PERMISSION.FLEX_ADD_EDIT_SHIFT)
			),
		[slot, userData.facilitiesEnabled, userData.permissionsByFacility]
	);

	const hasContract = !!slot.contract_id;
	const canGuaranteeSlot =
		!isFlex &&
		canGuarantee &&
		!slot.caregiver_id &&
		!slot.is_cancelled &&
		(!hasContract || moment.utc(slot.start_time_at_utc).isAfter(moment.utc()));
	const slotHasBeenWorked = slot.caregiver_id && slot.outcome_id;
	const HCPHasCheckedIn = slot.outcome_code === SHIFT_STATUS.ARRIVED.toUpperCase();
	const HCPHasConfirmed = slot.slot_status?.toUpperCase() === SHIFT_STATUS.CONFIRMED.toUpperCase();
	const canConfirmSlot = !isFlex && !slot.is_cancelled && slot.can_confirm && !slotHasBeenWorked && !HCPHasConfirmed;
	const isReplacement = !!slot.replaced_caregiver_name;
	const canCancelOnDemandSlot =
		!isFlex &&
		!slot.is_cancelled &&
		(slot.caregiver_id || !HCPHasConfirmed || isReplacement || hasContract) &&
		(!slot.outcome_id || HCPHasCheckedIn) &&
		(!slot.is_on_call || moment.utc(slot.start_time_at_utc).isAfter(moment.utc()));
	const canCancelFlexSlot = isFlex && !slot.outcome_id;
	const canAssignSlot =
		(isFlex || slot.is_hybrid) && moment.utc(slot.end_time_at_utc).isAfter(moment.utc()) && !slot.caregiver_id;
	const canEditAssignment = !isFlex && hasUnitsEnabled && hasAvailableUnits && hasEditAssignmentPermission;
	const canEditFlexAssignment = isFlex && hasUnitsEnabled && hasAvailableUnits && hasFlexEditUnitAssignmentPermission;

	const canRouteSlot =
		isFlex &&
		!slot.caregiver_id &&
		!!slot.enable_shift_routing &&
		moment.utc(slot.start_time_at_utc).isAfter(moment.utc());

	const canFacilityFloatSlot =
		!slot.is_orientation &&
		!!slot.caregiver_id &&
		userData.allowFlexFloating &&
		isFlex &&
		editFlexShiftFacilitiesForManagementCompany.length > 1 &&
		userData.managementCompanyPermissions[userData.activeFacility.management_company_id]?.includes(
			MANAGEMENT_COMPANIES_PERMISSIONS.FACILITY_FLEX_FLOAT
		);

	const canMarkSlotAsCallback =
		hasMarkAsCallbackPermission &&
		!!slot.caregiver_id &&
		!isFlex &&
		!!slot.is_on_call &&
		!slot.marked_as_callback &&
		!slot.on_call_slot_id;

	const hasSplitShiftManagementCompanyPermission = useMemo(
		() =>
			!!userData.managementCompanyPermissions[slot.management_company_id]?.includes(
				MANAGEMENT_COMPANIES_PERMISSIONS.FLEX_SPLIT_SHIFT
			),
		[userData.managementCompanyPermissions, slot.management_company_id]
	);

	const hasSplitShiftPermission = useMemo(
		() =>
			Utils.isAuthorizedExact(PERMISSION.FLEX_ADD_EDIT_SHIFT, permissions) && hasSplitShiftManagementCompanyPermission,
		[permissions, hasSplitShiftManagementCompanyPermission]
	);

	const allowSplitShifts = useMemo(() => {
		const matchingFacility = userData.facilitiesEnabled.find(
			(facility) => facility.management_company_id === slot.management_company_id
		);
		const { allow_split_shifts: allowSplit } = matchingFacility || {};
		return !!allowSplit;
	}, [userData.facilitiesEnabled, slot.management_company_id]);

	const canSplitSlot = isFlex && !!slot.caregiver_id && allowSplitShifts && hasSplitShiftPermission;

	const canEditSegmentUnitsOnly =
		isFlex &&
		!!slot.has_segments &&
		allowSplitShifts &&
		hasFlexEditUnitAssignmentPermission &&
		hasUnitsEnabled &&
		hasAvailableUnits;

	const hideUnitActionForSplitSlot = canSplitSlot && !!slot.has_segments;

	const getGuaranteeActionLabel = () => {
		if (hasContract) {
			return slot.is_guaranteed ? 'Remove Bundle Guarantee' : 'Guarantee Bundle';
		}
		return slot.is_guaranteed ? 'Remove Guarantee' : 'Guarantee Shift';
	};

	const getEditAssignmentLabel = () => {
		if (canEditSegmentUnitsOnly) {
			return 'Edit unit';
		}
		return slot.unit_id ? 'Update Unit' : 'Add Unit';
	};

	const getGuaranteeActionHandler = () => {
		if (hasContract) {
			return onGuaranteeBundleChange([slot]);
		}
		return onGuaranteedCheckboxChange([slot]);
	};

	const ACTION_ITEMS = useMemo(
		() => [
			{
				label: 'Confirm',
				value: 'CONFIRM',
				onSelect: () => onConfirm(slot),
				filter: () => hasEditStatusPermission && canConfirmSlot
			},
			{
				label: getEditAssignmentLabel(),
				value: 'EDIT_ASSIGNMENT',
				onSelect: () => {
					if (canEditSegmentUnitsOnly) {
						onSplitShift({ slot, unitsOnly: true });
						return;
					}
					setUnitAssignmentSlot({ unit_name: getUnitName(slot.unit_id), ...slot });
					setShowUnitAssignmentModal(
						HCPHasCheckedIn ? UNIT_ASSIGNMENT_MODALS.editArrived : UNIT_ASSIGNMENT_MODALS.edit
					);
				},
				filter: () =>
					(canEditAssignment || canEditFlexAssignment || canEditSegmentUnitsOnly) && !hideUnitActionForSplitSlot
			},
			{
				label: 'Remove Unit',
				value: 'REMOVE_ASSIGNMENT',
				onSelect: () => {
					setUnitAssignmentSlot({ unit_name: getUnitName(slot.unit_id), ...slot });
					setShowUnitAssignmentModal(
						HCPHasCheckedIn ? UNIT_ASSIGNMENT_MODALS.unassignArrived : UNIT_ASSIGNMENT_MODALS.unassign
					);
				},
				filter: () => (canEditAssignment || canEditFlexAssignment) && slot.unit_id && !slot.has_segments
			},
			{
				label: getGuaranteeActionLabel(),
				value: 'GUARANTEE_SLOT',
				onSelect: getGuaranteeActionHandler,
				filter: () => hasToggleGuaranteePermission && canGuaranteeSlot
			},
			{
				label: 'Mark as Called Back',
				value: 'MARK_AS_CALLBACK',
				onSelect: () => onMarkSlotAsCallback({ slot }),
				filter: () => canMarkSlotAsCallback
			},
			{
				label: 'Cancel',
				value: 'CANCEL',
				onSelect: () => onCancel(slot),
				filter: () => (hasFlexCancelPermission && canCancelFlexSlot) || (hasCancelPermission && canCancelOnDemandSlot)
			},
			{
				label: 'Assign',
				value: 'ASSIGN_CAREGIVER',
				onSelect: () => {
					onAssign({ unit_name: getUnitName(slot.unit_id), ...slot });
				},
				filter: () => hasFlexCaregiverAssignmentPermission && canAssignSlot
			},
			{
				label: 'Modify Route',
				value: 'MODIFY_ROUTE',
				onSelect: () => onRouteSlot({ slot }),
				filter: () => hasRouteFlexPermission && canRouteSlot
			},
			{
				label: 'Facility Float',
				value: 'FACILITY_FLOAT',
				onSelect: () => onFacilityFloatSlot({ slot }),
				filter: () => canFacilityFloatSlot
			},
			{
				label: slot.has_segments ? 'Edit split' : 'Split shift',
				value: 'SPLIT_SHIFT',
				onSelect: () => onSplitShift({ slot, unitsOnly: false }),
				filter: () => canSplitSlot
			}
		],
		[
			slot,
			onConfirm,
			hasEditStatusPermission,
			canConfirmSlot,
			setUnitAssignmentSlot,
			getUnitName,
			setShowUnitAssignmentModal,
			HCPHasCheckedIn,
			hasEditAssignmentPermission,
			canEditAssignment,
			canEditSegmentUnitsOnly,
			hideUnitActionForSplitSlot,
			onGuaranteedCheckboxChange,
			hasToggleGuaranteePermission,
			canGuaranteeSlot,
			onCancel,
			hasFlexCancelPermission,
			canCancelFlexSlot,
			hasCancelPermission,
			canCancelOnDemandSlot,
			canAssignSlot,
			hasRouteFlexPermission,
			canSplitSlot,
			onSplitShift
		]
	);

	const handleStatusChange = useCallback(
		({ updatedStatus: action }) => {
			const option = ACTION_ITEMS.find((_option) => _option.value === action);
			if (option) {
				option.onSelect();
			}
		},
		[ACTION_ITEMS]
	);

	const filteredOptions = ACTION_ITEMS.filter((option) => option.filter()).map((option) => ({
		label: option.label,
		value: option.value
	}));

	return (
		<Select
			className="select-status-daily-schedule"
			handleChange={handleStatusChange}
			isDisabled={!filteredOptions.length > 0}
			name="updatedStatus"
			options={filteredOptions}
			placeholder="Actions"
			value={slot.status}
			isSearchable={false}
			menuPlacement="bottom"
		/>
	);
};
export default ActionsDropdown;
